/**
 * Latitude / longitude in either format:
 *   decimal: 30.20807078635256, -97.74415147874039
 *   DMS:     30°12'29.1"N, 97°44'39.0"W  (spaces, ′ ″ and ’ ” also accepted;
 *            minutes and seconds are optional, e.g. 30°N or 30°12'N). A DMS
 *            value needs its direction (N/S, E/W) or a sign (-30°12'29.1"):
 *            without one, a forgotten W would put the point on the wrong side
 *            of the world.
 *
 * The form keeps what the user typed; the API gets a decimal text: a decimal
 * value exactly as typed, a DMS value converted (up to 7 decimals).
 */

const LIMIT = { lat: 90, lng: 180 };
const DIRECTIONS = { lat: ["N", "S"], lng: ["E", "W"] };

const DECIMAL = /^[+-]?\d{1,3}(\.\d+)?$/;
// degrees °, optional minutes ' (or ′ ’), optional seconds " (or ″ ” or ''), optional direction
const DMS =
  /^([+-])?\s*(\d{1,3}(?:\.\d+)?)\s*°\s*(?:(\d{1,2}(?:\.\d+)?)\s*['′’]\s*)?(?:(\d{1,2}(?:\.\d+)?)\s*(?:"|″|”|''|′′)\s*)?([NSEW])?$/i;

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

/**
 * Parse one coordinate.
 * @param {string|number} input
 * @param {"lat"|"lng"} axis
 * @returns {null | { value: number, format: "decimal"|"dms" } | { error: "format"|"missingDirection"|"direction"|"minutes"|"range" }}
 *          null for an empty value
 */
export const parseCoordinate = (input, axis) => {
  if (isBlank(input)) return null;
  const text = String(input).trim();

  if (DECIMAL.test(text)) {
    const value = Number(text);
    return Math.abs(value) > LIMIT[axis] ? { error: "range" } : { value, format: "decimal" };
  }

  const m = text.match(DMS);
  if (!m) return { error: "format" };
  const [, sign, deg, min = "0", sec = "0", dir] = m;
  if (dir && !DIRECTIONS[axis].includes(dir.toUpperCase())) return { error: "direction" };
  if (dir && sign) return { error: "format" }; // "-30°N": sign or direction, not both
  if (!dir && !sign) return { error: "missingDirection" }; // 30°12'29.1": N or S?
  if (Number(min) >= 60 || Number(sec) >= 60) return { error: "minutes" };
  const abs = Number(deg) + Number(min) / 60 + Number(sec) / 3600;
  if (abs > LIMIT[axis]) return { error: "range" };
  const negative = sign === "-" || ["S", "W"].includes(dir?.toUpperCase());
  return { value: negative ? -abs : abs, format: "dms" };
};

/**
 * The text sent to the API: decimal values exactly as typed (trimmed), DMS
 * values as decimal text with up to 7 decimals. Empty or invalid values are
 * returned unchanged (validation blocks the save).
 */
export const toApiCoordinate = (input, axis) => {
  const parsed = parseCoordinate(input, axis);
  if (!parsed || parsed.error) return input;
  if (parsed.format === "decimal") return String(input).trim();
  return String(Number(parsed.value.toFixed(7)));
};

/**
 * Split a pasted pair into [latitude, longitude]:
 *   "30.208, -97.744", "30.208 -97.744", "30°12'29.1"N 97°44'39.0"W",
 *   "30°12'29.1"N, 97°44'39.0"W". Returns null when it isn't a pair.
 */
export const splitCoordinatePair = (text) => {
  const t = String(text ?? "").trim();
  if (!t) return null;
  if (t.includes(",")) {
    const parts = t.split(",").map((s) => s.trim());
    return parts.length === 2 && parts.every(Boolean) ? parts : null;
  }
  // DMS pair: latitude ends with N/S, longitude follows
  const dms = t.match(/^(.+?[NS])\s+(.+)$/i);
  if (dms) return [dms[1].trim(), dms[2].trim()];
  // two decimals separated by spaces
  const nums = t.split(/\s+/);
  return nums.length === 2 && nums.every((n) => DECIMAL.test(n)) ? nums : null;
};

/**
 * react-hook-form rules (see components/Form/rhf/rules.js) for a coordinate:
 * each failure has its own error key so the field can say what is wrong.
 */
export const coordinateRules = (axis, required = false) => {
  const fails = (key) => (value) => parseCoordinate(value, axis)?.error !== key;
  return {
    ...(required ? { required: true } : {}),
    validate: {
      coordinateFormat: fails("format"),
      coordinateMissingDirection: fails("missingDirection"),
      coordinateDirection: fails("direction"),
      coordinateMinutes: fails("minutes"),
      coordinateRange: fails("range"),
    },
  };
};
